import { useEffect, useState } from 'react'
import { ApplicationQuestionnaire, customerData } from '../../../utilities/CustomerData'
import { Autocomplete, useLoadScript } from '@react-google-maps/api'
import { apiURL } from '../../../utilities/URLs'

export function AdvancePaymentQuestionnaire({ handleCompleteQuestionnaire }) {
  const [applicationQuestionnaire, setApplicationQuestionnaire] = useState(new ApplicationQuestionnaire())
  const [spouseApplicationQuestionnaire, setSpouseApplicationQuestionnaire] = useState(new ApplicationQuestionnaire())
  const [hasEIN, setHasEIN] = useState(false)
  const [spouseHasEIN, setSpouseHasEIN] = useState(false)
  const [loading, setLoading] = useState(true)
  const [autocomplete, setAutocomplete] = useState(null)

  const libraries = ['places']

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  })

  const handleLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance)
  }

  const handlePlaceChanged = () => {
    const getAddressComponent = (type, useShortName = false) => {
      const component = place.address_components.find(comp => comp.types.includes(type))
      return component ? (useShortName ? component.short_name : component.long_name) : ''
    }

    const place = autocomplete.getPlace()
    if (place) {
      setApplicationQuestionnaire((prevApplication) => ({
        ...prevApplication,
        entityStreet: `${getAddressComponent('street_number')} ${getAddressComponent('route')}`.trim(),
        entityCity: getAddressComponent('locality'),
        entityState: getAddressComponent('administrative_area_level_1', true),
        entityZip: getAddressComponent('postal_code')
      }))
    }
  }

  const handleSpousePlaceChanged = () => {
    const getAddressComponent = (type, useShortName = false) => {
      const component = place.address_components.find(comp => comp.types.includes(type))
      return component ? (useShortName ? component.short_name : component.long_name) : ''
    }

    const place = autocomplete.getPlace()
    if (place) {
      setSpouseApplicationQuestionnaire((prevApplication) => ({
        ...prevApplication,
        entityStreet: `${getAddressComponent('street_number')} ${getAddressComponent('route')}`.trim(),
        entityCity: getAddressComponent('locality'),
        entityState: getAddressComponent('administrative_area_level_1', true),
        entityZip: getAddressComponent('postal_code')
      }))
    }
  }

  useEffect(() => {
    const handleCheckForEIN = async () => {
      const einResponse = await fetch(`${apiURL}/api/checkForEIN?customerId=${encodeURIComponent(customerData.customerLeadData.leadContact.contactId)}`, {
        headers: {
          'Authorization': customerData.accessToken
        }
      })
      const einData = await einResponse.json()
      setLoading(false)
      if(einData.response.error)
        console.error(einData.response.error)
      else {
        setHasEIN(einData.response.hasEIN)
        setSpouseHasEIN(einData.response.spouseHasEIN)
      }
    }

    handleCheckForEIN()
  }, [])

  return (
    <div className='w-full flex flex-col items-center mt-4'>
      <p className='text-2xl font-bold mb-4'>Application Questionnaire</p>
      {!loading ? (
        <div className='lg:w-full w-11/12 flex flex-col items-center'>
          <p>Please answer a few questions about your work/business.</p>
          <div className='lg:w-1/2 h-full flex flex-row items-stretch justify-center gap-88 mt-10 flex-1'>
            <div className='w-full'>
              <div className='flex flex-col mb-4'>
                <p className='font-bold text-xl'>Primary Information</p>
                <p className='font-bold'>Business Type</p>
                <div className='flex flex-row flex-wrap items-center gap-4'>
                  <div onClick={() => setApplicationQuestionnaire((prevApplication) => ({
                    ...prevApplication,
                    entityType: 'Limited Partnership',
                  }))} className='cursor-pointer flex flex-row items-center gap-2'>
                    <input 
                      type='checkbox'
                      checked={applicationQuestionnaire.entityType === 'Limited Partnership'}
                      onChange={() => setApplicationQuestionnaire((prevApplication) => ({
                        ...prevApplication,
                        entityType: 'Limited Partnership',
                      }))}
                    />
                    <p>Limited Partnership</p>
                  </div>
                  <div onClick={() => setApplicationQuestionnaire((prevApplication) => ({ ...prevApplication, entityType: 'Limited Liability Co.' }))} className='cursor-pointer flex flex-row items-center gap-2'>
                    <input 
                      type='checkbox'
                      checked={applicationQuestionnaire.entityType === 'Limited Liability Co.'}
                      onChange={() => setApplicationQuestionnaire((prevApplication) => ({
                        ...prevApplication,
                        entityType: 'Limited Liability Co.'
                      }))}
                    />
                    <p>Limited Liability Co.</p>
                  </div>
                  <div onClick={() => setApplicationQuestionnaire((prevApplication) => ({
                    ...prevApplication,
                    entityType: 'Sole Proprietorship',
                  }))} className='cursor-pointer flex flex-row items-center gap-2'>
                    <input 
                      type='checkbox'
                      checked={applicationQuestionnaire.entityType === 'Sole Proprietorship'}
                      onChange={() => setApplicationQuestionnaire((prevApplication) => ({
                        ...prevApplication,
                        entityType: 'Sole Proprietorship',
                      }))}
                    />
                    <p>Sole Proprietorship</p>
                  </div>
                </div>
              </div>
              <div className='relative mb-4'>
                <p className='absolute text-sm bg-white left-3 top-[-5px]'>Profession</p>
                <input
                  type='text'
                  name='natureOfBusiness'
                  placeholder='Profession'
                  value={applicationQuestionnaire.natureOfBusiness}
                  onChange={(e) => setApplicationQuestionnaire((prevApplication) => ({
                    ...prevApplication,
                    natureOfBusiness: e.target.value
                  }))}
                  className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                />
              </div>
              {(hasEIN || applicationQuestionnaire.entityType === 'Limited Liability Co.') && (
                <div>
                  <div className='relative mb-4'>
                    <p className='absolute text-sm bg-white left-3 top-[-5px]'>EIN</p>
                    <input
                      type='text'
                      name='ein'
                      placeholder='XX-XXXXXXX'
                      value={applicationQuestionnaire.ein}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '')
                        if (value.length > 2) {
                          value = `${value.slice(0, 2)}-${value.slice(2, 9)}`
                        }
                        setApplicationQuestionnaire((prevApplication) => ({
                          ...prevApplication,
                          ein: value
                        }))
                      }}
                      maxLength={10}
                      className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                    />
                  </div>
                  <div className='relative mb-4'>
                    <p className='absolute text-sm bg-white left-3 top-[-5px]'>Business Name</p>
                    <input
                      type='text'
                      name='entityName'
                      placeholder='Business Name'
                      value={applicationQuestionnaire.entityName}
                      onChange={(e) => {
                        setApplicationQuestionnaire((prevApplication) => ({
                          ...prevApplication,
                          entityName: e.target.value
                        }))
                      }}
                      className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                    />
                  </div>
                  <div className="relative mb-4">
                    <p className="absolute text-sm bg-white left-3 top-[-5px]">Business Phone</p>
                    <input
                      type="text"
                      name="entityPhone"
                      placeholder="XXX-XXX-XXXX"
                      value={applicationQuestionnaire.entityPhone}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, "")
                        if (value.length > 10) value = value.slice(0, 10)
                        if (value.length >= 7) {
                          value = `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`
                        } else if (value.length >= 4) {
                          value = `${value.slice(0, 3)}-${value.slice(3)}`
                        }
                        setApplicationQuestionnaire((prevApplication) => ({
                          ...prevApplication,
                          entityPhone: value,
                        }))
                      }}
                      className="mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300"
                      maxLength={12}
                    />
                  </div>
                  {isLoaded && (
                    <div className='relative mb-4'>
                      <p className='absolute text-sm bg-white left-3 top-[-5px]'>Business Street</p>
                      <Autocomplete onLoad={handleLoad} onPlaceChanged={handlePlaceChanged}>
                        <input
                          type='text'
                          name='entityStreet'
                          placeholder='Business Street'
                          value={applicationQuestionnaire.entityStreet}
                          onChange={(e) => {
                            setApplicationQuestionnaire((prevApplication) => ({
                              ...prevApplication,
                              entityStreet: e.target.value
                            }))
                          }}
                          className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                        />
                      </Autocomplete>
                    </div>
                  )}
                  <div className='relative mb-4'>
                    <p className='absolute text-sm bg-white left-3 top-[-5px]'>Business City</p>
                    <input
                      type='text'
                      name='entityCity'
                      placeholder='Business City'
                      value={applicationQuestionnaire.entityCity}
                      onChange={(e) => {
                        setApplicationQuestionnaire((prevApplication) => ({
                          ...prevApplication,
                          entityCity: e.target.value
                        }))
                      }}
                      className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                    />
                  </div>
                  <div className='relative mb-4'>
                    <p className='absolute text-sm bg-white left-3 top-[-5px]'>Business State</p>
                    <input
                      type='text'
                      name='entityState'
                      placeholder='Business State'
                      value={applicationQuestionnaire.entityState}
                      onChange={(e) => {
                        setApplicationQuestionnaire((prevApplication) => ({
                          ...prevApplication,
                          entityState: e.target.value
                        }))
                      }}
                      className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                    />
                  </div>
                  <div className='relative mb-4'>
                    <p className='absolute text-sm bg-white left-3 top-[-5px]'>Business Zip</p>
                    <input
                      type='text'
                      name='entityZip'
                      placeholder='Business Zip'
                      value={applicationQuestionnaire.entityZip}
                      onChange={(e) => {
                        setApplicationQuestionnaire((prevApplication) => ({
                          ...prevApplication,
                          entityZip: e.target.value
                        }))
                      }}
                      className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                    />
                  </div>
                </div>
              )}
              {customerData.customerMultipleSourceIncome.didSpouseCalculation && (
                <div>
                  <div className='flex flex-col mb-4'>
                    <p className='font-bold text-xl'>Spouse Information</p>
                    <p className='font-bold'>Business Type</p>
                    <div className='flex flex-row flex-wrap items-center gap-4'>
                      <div onClick={() => setSpouseApplicationQuestionnaire((prevApplication) => ({
                        ...prevApplication,
                        entityType: 'Limited Partnership',
                      }))} className='cursor-pointer flex flex-row items-center gap-2'>
                        <input 
                          type='checkbox'
                          checked={setApplicationQuestionnaire.entityType === 'Limited Partnership'}
                          onChange={() => setSpouseApplicationQuestionnaire((prevApplication) => ({
                            ...prevApplication,
                            entityType: 'Limited Partnership',
                          }))}
                        />
                        <p>Limited Partnership</p>
                      </div>
                      <div onClick={() => setSpouseApplicationQuestionnaire((prevApplication) => ({ ...prevApplication, entityType: 'Limited Liability Co.' }))} className='cursor-pointer flex flex-row items-center gap-2'>
                        <input 
                          type='checkbox'
                          checked={spouseApplicationQuestionnaire.entityType === 'Limited Liability Co.'}
                          onChange={() => setSpouseApplicationQuestionnaire((prevApplication) => ({
                            ...prevApplication,
                            entityType: 'Limited Liability Co.'
                          }))}
                        />
                        <p>Limited Liability Co.</p>
                      </div>
                      <div onClick={() => setSpouseApplicationQuestionnaire((prevApplication) => ({
                        ...prevApplication,
                        entityType: 'Sole Proprietorship',
                      }))} className='cursor-pointer flex flex-row items-center gap-2'>
                        <input 
                          type='checkbox'
                          checked={spouseApplicationQuestionnaire.entityType === 'Sole Proprietorship'}
                          onChange={() => setSpouseApplicationQuestionnaire((prevApplication) => ({
                            ...prevApplication,
                            entityType: 'Sole Proprietorship',
                          }))}
                        />
                        <p>Sole Proprietorship</p>
                      </div>
                    </div>
                  </div>
                  <div className='relative mb-4'>
                    <p className='absolute text-sm bg-white left-3 top-[-5px]'>Profession</p>
                    <input
                      type='text'
                      name='natureOfBusiness'
                      placeholder='Profession'
                      value={spouseApplicationQuestionnaire.natureOfBusiness}
                      onChange={(e) => setSpouseApplicationQuestionnaire((prevApplication) => ({
                        ...prevApplication,
                        natureOfBusiness: e.target.value
                      }))}
                      className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                    />
                  </div>
                  {(hasEIN || spouseApplicationQuestionnaire.entityType === 'Limited Liability Co.') && (
                    <div>
                      <div className='relative mb-4'>
                        <p className='absolute text-sm bg-white left-3 top-[-5px]'>EIN</p>
                        <input
                          type='text'
                          name='ein'
                          placeholder='XX-XXXXXXX'
                          value={spouseApplicationQuestionnaire.ein}
                          onChange={(e) => {
                            let value = e.target.value.replace(/\D/g, '')
                            if (value.length > 2) {
                              value = `${value.slice(0, 2)}-${value.slice(2, 9)}`
                            }
                            setSpouseApplicationQuestionnaire((prevApplication) => ({
                              ...prevApplication,
                              ein: value
                            }))
                          }}
                          maxLength={10}
                          className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                        />
                      </div>
                      <div className='relative mb-4'>
                        <p className='absolute text-sm bg-white left-3 top-[-5px]'>Business Name</p>
                        <input
                          type='text'
                          name='entityName'
                          placeholder='Business Name'
                          value={spouseApplicationQuestionnaire.entityName}
                          onChange={(e) => {
                            setSpouseApplicationQuestionnaire((prevApplication) => ({
                              ...prevApplication,
                              entityName: e.target.value
                            }))
                          }}
                          className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                        />
                      </div>
                      <div className="relative mb-4">
                        <p className="absolute text-sm bg-white left-3 top-[-5px]">Business Phone</p>
                        <input
                          type="text"
                          name="entityPhone"
                          placeholder="XXX-XXX-XXXX"
                          value={spouseApplicationQuestionnaire.entityPhone}
                          onChange={(e) => {
                            let value = e.target.value.replace(/\D/g, "")
                            if (value.length > 10) value = value.slice(0, 10)
                            if (value.length >= 7) {
                              value = `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`
                            } else if (value.length >= 4) {
                              value = `${value.slice(0, 3)}-${value.slice(3)}`
                            }
                            setSpouseApplicationQuestionnaire((prevApplication) => ({
                              ...prevApplication,
                              entityPhone: value,
                            }))
                          }}
                          className="mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300"
                          maxLength={12}
                        />
                      </div>
                      {isLoaded && (
                        <div className='relative mb-4'>
                          <p className='absolute text-sm bg-white left-3 top-[-5px]'>Business Street</p>
                          <Autocomplete onLoad={handleLoad} onPlaceChanged={handleSpousePlaceChanged}>
                            <input
                              type='text'
                              name='entityStreet'
                              placeholder='Business Street'
                              value={spouseApplicationQuestionnaire.entityStreet}
                              onChange={(e) => {
                                setSpouseApplicationQuestionnaire((prevApplication) => ({
                                  ...prevApplication,
                                  entityStreet: e.target.value
                                }))
                              }}
                              className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                            />
                          </Autocomplete>
                        </div>
                      )}
                      <div className='relative mb-4'>
                        <p className='absolute text-sm bg-white left-3 top-[-5px]'>Business City</p>
                        <input
                          type='text'
                          name='entityCity'
                          placeholder='Business City'
                          value={spouseApplicationQuestionnaire.entityCity}
                          onChange={(e) => {
                            setSpouseApplicationQuestionnaire((prevApplication) => ({
                              ...prevApplication,
                              entityCity: e.target.value
                            }))
                          }}
                          className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                        />
                      </div>
                      <div className='relative mb-4'>
                        <p className='absolute text-sm bg-white left-3 top-[-5px]'>Business State</p>
                        <input
                          type='text'
                          name='entityState'
                          placeholder='Business State'
                          value={spouseApplicationQuestionnaire.entityState}
                          onChange={(e) => {
                            setSpouseApplicationQuestionnaire((prevApplication) => ({
                              ...prevApplication,
                              entityState: e.target.value
                            }))
                          }}
                          className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                        />
                      </div>
                      <div className='relative mb-4'>
                        <p className='absolute text-sm bg-white left-3 top-[-5px]'>Business Zip</p>
                        <input
                          type='text'
                          name='entityZip'
                          placeholder='Business Zip'
                          value={spouseApplicationQuestionnaire.entityZip}
                          onChange={(e) => {
                            setSpouseApplicationQuestionnaire((prevApplication) => ({
                              ...prevApplication,
                              entityZip: e.target.value
                            }))
                          }}
                          className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className='flex w-full justify-center'>
                <button
                  disabled={
                    !applicationQuestionnaire.entityType ||
                    !applicationQuestionnaire.natureOfBusiness ||
                    ((hasEIN || applicationQuestionnaire.entityType === 'Limited Liability Co.') &&
                      (!applicationQuestionnaire.entityName ||
                        !applicationQuestionnaire.entityStreet ||
                        !applicationQuestionnaire.entityCity ||
                        !applicationQuestionnaire.entityState ||
                        !applicationQuestionnaire.entityZip ||
                        !applicationQuestionnaire.entityPhone ||
                        !applicationQuestionnaire.ein ||
                        applicationQuestionnaire.ein.length !== 10))
                  }
                  onClick={() => handleCompleteQuestionnaire(applicationQuestionnaire, spouseApplicationQuestionnaire)}
                  className={`px-8 py-2 mt-4 text-white rounded max-sm:mb-4 transition duration-200 ${
                    !applicationQuestionnaire.entityType ||
                    !applicationQuestionnaire.natureOfBusiness ||
                    ((hasEIN || applicationQuestionnaire.entityType === 'Limited Liability Co.') &&
                      (!applicationQuestionnaire.entityName ||
                        !applicationQuestionnaire.entityStreet ||
                        !applicationQuestionnaire.entityCity ||
                        !applicationQuestionnaire.entityState ||
                        !applicationQuestionnaire.entityZip ||
                        !applicationQuestionnaire.entityPhone ||
                        !applicationQuestionnaire.ein ||
                        applicationQuestionnaire.ein.length !== 10))
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-primaryColor hover:bg-primaryDarkColor'
                  }`}
                >
                  Next
                </button>
              </div>
              <p className='text-sm text-gray-500 italic text-center mt-12 mb-10'>Rest Assured, all the data you provide will be encrypted and stored securely. This information is only used to process your file.</p>
            </div>
          </div>
        </div>
      ) : (
        <div className='flex flex-row items-center justify-center gap-2'>
          <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
          <p>Loading...</p>
        </div>
      )}
    </div>
  )
}